import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

const NavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: #000000;
  border-top: 1px solid #333333;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 8px;
  z-index: 1000;
  
  /* Safe area for iOS */
  padding-bottom: env(safe-area-inset-bottom);
  
  /* Desktop PWA constraint */
  @media (min-width: 768px) {
    max-width: 414px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const NavButton = styled.button<{ $isActive: boolean; $isCenter?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: ${props => props.$isActive ? '#a855f7' : '#ffffff'};
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 12px;
  transition: all 0.2s ease;
  flex: 1;
  max-width: ${props => props.$isCenter ? '80px' : 'none'};
  min-height: 56px;
  position: relative;
  
  &:hover {
    background: #333333;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  ${props => props.$isActive && `
    background: #000000;
    border: 1px solid #333333;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 3px;
      background: #a855f7;
      border-radius: 0 0 3px 3px;
    }
  `}
  
  ${props => props.$isCenter && `
    background: #000000;
    border: 1px solid #333333;
    border-radius: 16px;
    min-width: 64px;
    max-width: 64px;
    height: 64px;
    
    ${props.$isActive && `
      border-color: #444444;
    `}
  `}
`;

const NavIcon = styled.i<{ $isCenter?: boolean }>`
  font-size: ${props => props.$isCenter ? '24px' : '20px'};
  color: inherit;
`;

const NavLabel = styled.span<{ $isCenter?: boolean }>`
  font-size: 10px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: inherit;
  display: ${props => props.$isCenter ? 'none' : 'block'};
`;

interface NavItem {
  path: string;
  icon: string;
  label: string;
  isCenter?: boolean;
}

const navItems: NavItem[] = [
  { path: '/pwa/explore', icon: 'fa-search', label: 'Explore' },
  { path: '/pwa/locks', icon: 'fa-lock', label: 'Locks' },
  { path: '/pwa/create', icon: 'fa-plus', label: 'Create', isCenter: true },
  { path: '/pwa/swap', icon: 'fa-exchange-alt', label: 'Swap' },
  { path: '/pwa/profile', icon: 'fa-user', label: 'Profile' }
];

const PWABottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  return (
    <NavContainer>
      {navItems.map((item) => {
        const isActive = location.pathname === item.path || 
          (item.path === '/pwa/swap' && location.pathname.startsWith('/pwa/swap'));
        
        return (
          <NavButton
            key={item.path}
            $isActive={isActive}
            $isCenter={item.isCenter}
            onClick={() => handleNavClick(item.path)}
            aria-label={item.label}
          >
            <NavIcon className={`fas ${item.icon}`} $isCenter={item.isCenter} />
            <NavLabel $isCenter={item.isCenter}>{item.label}</NavLabel>
          </NavButton>
        );
      })}
    </NavContainer>
  );
};

export default PWABottomNav;

